import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb, { getCategoryBreadcrumb } from '@/components/feature/Breadcrumb';
import { articles, categories } from '@/mocks/data';

const articleHeroImages: Record<string, string> = {
  '91n-com-zuixin-rukou-dizhi': 'https://readdy.ai/api/search-image?query=Modern%20secure%20network%20access%20gateway%20with%20glowing%20portal%20entrance%2C%20clean%20minimal%20tech%20aesthetic%2C%20warm%20amber%20and%20white%20tones%2C%20abstract%20geometric%20shapes%2C%20wide%20banner%20format&width=1600&height=400&seq=article-hero-91n-com-zuixin-rukou-dizhi&orientation=landscape',
  '91n-com-xinshou-jiaocheng': 'https://readdy.ai/api/search-image?query=Friendly%20beginner%20guide%20illustration%20with%20step%20by%20step%20tutorial%20visuals%2C%20warm%20welcoming%20colors%2C%20wide%20banner%20format%2C%20soft%20background&width=1600&height=400&seq=article-hero-91n-com-xinshou-jiaocheng&orientation=landscape',
  '91n-com-changjian-wenti-jieda': 'https://readdy.ai/api/search-image?query=Help%20desk%20and%20FAQ%20concept%20with%20question%20marks%2C%20wide%20banner%20format%2C%20warm%20amber%20and%20neutral%20tones%2C%20professional%20support%20atmosphere&width=1600&height=400&seq=article-hero-91n-com-changjian-wenti-jieda&orientation=landscape',
  '91n-com-anquan-fangwen-zhinan': 'https://readdy.ai/api/search-image?query=Cybersecurity%20shield%20concept%20with%20protective%20layers%2C%20wide%20banner%20format%2C%20professional%20security%20theme%2C%20warm%20accents&width=1600&height=400&seq=article-hero-91n-com-anquan-fangwen-zhinan&orientation=landscape',
  '91n-com-pingtai-jieshao': 'https://readdy.ai/api/search-image?query=Modern%20digital%20platform%20overview%20with%20interconnected%20features%2C%20wide%20banner%20format%2C%20warm%20amber%20highlights&width=1600&height=400&seq=article-hero-91n-com-pingtai-jieshao&orientation=landscape',
  '91n-com-yidongduan-jiaocheng': 'https://readdy.ai/api/search-image?query=Smartphone%20displaying%20modern%20web%20interface%2C%20wide%20banner%20format%2C%20warm%20lighting%2C%20professional%20tech%20photography&width=1600&height=400&seq=article-hero-91n-com-yidongduan-jiaocheng&orientation=landscape',
  '91n-com-zhanghao-zhuche-denglu': 'https://readdy.ai/api/search-image?query=Account%20login%20interface%20concept%20with%20key%20and%20user%20icon%2C%20wide%20banner%20format%2C%20warm%20amber%20highlights%2C%20professional%20authentication%20theme&width=1600&height=400&seq=article-hero-91n-com-zhanghao-zhuche-denglu&orientation=landscape',
  '91n-com-banben-gengxin-rizhi': 'https://readdy.ai/api/search-image?query=Version%20update%20changelog%20concept%2C%20wide%20banner%20format%2C%20warm%20amber%20accent%20details%2C%20organized%20documentation%20layout&width=1600&height=400&seq=article-hero-91n-com-banben-gengxin-rizhi&orientation=landscape',
  '91n-com-lianxi-kefu-fangshi': 'https://readdy.ai/api/search-image?query=Customer%20support%20contact%20center%20concept%20with%20communication%20icons%2C%20wide%20banner%20format%2C%20warm%20tones%2C%20friendly%20service%20atmosphere&width=1600&height=400&seq=article-hero-91n-com-lianxi-kefu-fangshi&orientation=landscape',
  '91n-com-wangluo-huanjing-youhua': 'https://readdy.ai/api/search-image?query=Network%20speed%20optimization%20concept%20with%20connection%20nodes%2C%20wide%20banner%20format%2C%20warm%20amber%20highlights&width=1600&height=400&seq=article-hero-91n-com-wangluo-huanjing-youhua&orientation=landscape',
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500">
              <i className="ri-file-unknow-line text-3xl"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-950 mb-2">文章未找到</h1>
            <p className="text-foreground-600 text-sm mb-6">该文章不存在或已被移除</p>
            <Link to="/" className="inline-flex items-center gap-1 px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-home-4-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    author: {
      '@type': 'Organization',
      name: '91n com 官方入口指南',
    },
    publisher: {
      '@type': 'Organization',
      name: '91n com 官方入口指南',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://91n-guide.com/article/${article.slug}`,
    },
  };

  const breadcrumbItems = getCategoryBreadcrumb(article.category, article.title);

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navbar />
      <main>
        <section className="relative w-full h-[280px] md:h-[360px] flex items-center justify-center overflow-hidden">
          <img
            src={articleHeroImages[article.slug] || ''}
            alt={article.title}
            title={`${article.title} - 91n com 官方入口指南`}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/30"></div>
          <div className="relative z-10 text-center px-4 max-w-3xl">
            {category && (
              <Link
                to={`/category/${category.slug}`}
                className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-background-50/20 backdrop-blur-sm border border-background-50/30 text-background-50 text-xs font-medium mb-4 cursor-pointer hover:bg-background-50/30 transition-colors"
              >
                <i className={`${category.icon} text-xs`}></i>
                {category.name}
              </Link>
            )}
            <h1 className="text-xl md:text-2xl font-bold text-background-50 mb-3 leading-snug">{article.title}</h1>
            <div className="flex items-center justify-center gap-3 text-background-50/70 text-xs">
              <span>{article.date}</span>
              <span>&middot;</span>
              <span>{article.readTime}</span>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="mb-8">
              <Breadcrumb items={breadcrumbItems} />
            </div>

            <div className="flex flex-col lg:flex-row gap-10">
              <article className="flex-1 min-w-0">
                <div
                  className="article-content text-sm md:text-base"
                  dangerouslySetInnerHTML={{
                    __html: article.content
                      .split('\n')
                      .map((line) => {
                        const trimmed = line.trim();
                        if (trimmed.startsWith('## ')) {
                          const title = trimmed.replace('## ', '');
                          const id = article.contentSections.find((s) => s.title === title)?.id || '';
                          return `<h2 id="${id}">${title}</h2>`;
                        }
                        if (trimmed.startsWith('### ')) {
                          const title = trimmed.replace('### ', '');
                          return `<h3>${title}</h3>`;
                        }
                        if (trimmed.startsWith('- ')) {
                          return `<li>${trimmed.replace('- ', '')}</li>`;
                        }
                        if (trimmed.match(/^\d+\./)) {
                          return `<li>${trimmed.replace(/^\d+\.\s*/, '')}</li>`;
                        }
                        if (trimmed === '') return '';
                        return `<p>${trimmed}</p>`;
                      })
                      .join('\n')
                      .replace(/(<li>.*<\/li>\n?)+/g, (match) => `<ul>${match}</ul>`),
                  }}
                />

                <div className="mt-10 pt-6 border-t border-background-200/70">
                  <div className="flex flex-wrap gap-2">
                    {article.tags.map((tag) => (
                      <span key={tag} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs font-medium">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </article>

              <aside className="lg:w-72 flex-shrink-0">
                <div className="lg:sticky lg:top-24 space-y-6">
                  <div className="p-5 rounded-xl bg-background-100 border border-background-200/70">
                    <h4 className="font-semibold text-foreground-950 mb-4 text-sm flex items-center gap-2">
                      <i className="ri-list-check-2 text-primary-500"></i>
                      目录
                    </h4>
                    <nav className="space-y-1">
                      {article.contentSections.map((section) => (
                        <a
                          key={section.id}
                          href={`#${section.id}`}
                          className="block text-foreground-600 hover:text-primary-600 text-sm py-1.5 transition-colors cursor-pointer"
                        >
                          {section.title}
                        </a>
                      ))}
                    </nav>
                  </div>

                  {relatedArticles.length > 0 && (
                    <div className="p-5 rounded-xl bg-background-100 border border-background-200/70">
                      <h4 className="font-semibold text-foreground-950 mb-4 text-sm flex items-center gap-2">
                        <i className="ri-links-line text-primary-500"></i>
                        相关文章
                      </h4>
                      <div className="space-y-3">
                        {relatedArticles.map((rel) => (
                          <Link
                            key={rel.slug}
                            to={`/article/${rel.slug}`}
                            className="block group cursor-pointer"
                          >
                            <h5 className="text-sm text-foreground-700 group-hover:text-primary-600 transition-colors leading-snug line-clamp-2">
                              {rel.title}
                            </h5>
                            <span className="text-foreground-400 text-xs mt-1">{rel.date}</span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </aside>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}